import React from 'react';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import AppDialog, { AppDialogHeader } from '../common/AppDialog';
import StatusChip from '../common/StatusChip';
import { ReceiptLongOutlined as ReceiptIcon } from '@mui/icons-material';
import { Sale, PaymentStatus } from '../../types';
import { useLanguage } from '../../contexts/LanguageContext';
import { formatCurrency } from '../../utils/currency';
import { formatDateTime } from '../../utils/date';
import SaleItemsBreakdown from './SaleItemsBreakdown';

interface SaleDetailDialogProps {
  open: boolean;
  sale: Sale | null;
  onClose: () => void;
}

const getPaymentStatusColor = (status: PaymentStatus): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  switch (status) {
    case 'PAID': return 'success';
    case 'PARTIAL': return 'warning';
    case 'UNPAID': return 'error';
    default: return 'default';
  }
};

const SaleDetailDialog: React.FC<SaleDetailDialogProps> = ({ open, sale, onClose }) => {
  const { t, language } = useLanguage();

  return (
    <AppDialog presentation="workflow" open={open} onClose={onClose} maxWidth="md" fullWidth aria-labelledby="sale-detail-title">
      <AppDialogHeader id="sale-detail-title" icon={<ReceiptIcon />} subtitle={sale?.saleNumber}>
        {t('sales.saleDetails')}
      </AppDialogHeader>
      <DialogContent>
        {sale && (
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>{sale.saleNumber}</Typography>
            <Box sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 2, bgcolor: 'action.hover', border: theme => `1px solid ${theme.palette.divider}` }}>
              <Typography variant="body2" color="text.secondary">{t('sales.totalAmount')}</Typography>
              <Typography variant="h4" color="primary.main" fontWeight={700}>{formatCurrency(sale.totalAmount)}</Typography>
              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('sales.netAmount')}: <strong>{formatCurrency(sale.netAmount ?? sale.totalAmount - (sale.vatAmount ?? 0))}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('sales.vatAmount')}: <strong>{formatCurrency(sale.vatAmount ?? 0)}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('sales.grossAmount')}: <strong>{formatCurrency(sale.totalAmount)}</strong>
                </Typography>
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }}>
                <Box sx={{ flex: 1 }}><Typography variant="caption" color="text.secondary">{t('sales.paidAmount')}</Typography><Typography fontWeight={700} color={sale.paidAmount > 0 ? 'success.main' : 'text.secondary'}>{formatCurrency(sale.paidAmount)}</Typography></Box>
                <Box sx={{ flex: 1 }}><Typography variant="caption" color="text.secondary">{t('sales.remainingAmount')}</Typography><Typography fontWeight={700} color={sale.remainingAmount > 0 ? 'error.main' : 'text.secondary'}>{formatCurrency(sale.remainingAmount)}</Typography></Box>
                <Box sx={{ flex: 1 }}><Typography variant="caption" color="text.secondary">{t('sales.paymentStatus')}</Typography><Box><StatusChip label={sale.paymentStatus === PaymentStatus.PAID ? t('sales.paid') : sale.paymentStatus === PaymentStatus.PARTIAL ? t('sales.partiallyPaid') : t('sales.unpaid')} color={getPaymentStatusColor(sale.paymentStatus)} /></Box></Box>
              </Stack>
            </Box>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('sales.customer')}
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {sale.customerName}
                </Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('sales.saleDate')}
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {formatDateTime(sale.saleDate, language, {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </Typography>
              </Box>
            </Stack>

            <Divider />
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>{t('sales.createdBy')}</Typography>
                <Typography variant="body1" fontWeight="medium">{sale.createdByUsername}</Typography>
              </Box>
            </Stack>

            {sale.saleItems && sale.saleItems.length > 0 && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>{t('sales.items')}</Typography>
                <SaleItemsBreakdown items={sale.saleItems} ariaLabel={t('sales.items')} />
              </Box>
            )}

            {sale.notes && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>{t('sales.notes')}</Typography>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                  <Typography variant="body2">{sale.notes}</Typography>
                </Paper>
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.close')}</Button>
      </DialogActions>
    </AppDialog>
  );
};

export default SaleDetailDialog;
